import React from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { KpiCard, ExportMenu, ScreenHeader, EmptyMeter } from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { saveManualCost, deleteManualCost, materializeManualCosts, convertToINR } from '@/lib/ops-data.js';
import { fmtINR, fmtMoney, fmtMonthLabel, todayIso } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// Manual costs — the non-metered spend (store fees, infra baselines, subscriptions).
// CRUD over Basic manual-cost (§4); recurring entries materialize per-month at read
// time (monthly = full each month, yearly = amortized /12, one-time = start month).
const CATEGORIES = ['store', 'infra', 'subscription', 'other'];
const CADENCES = ['one-time', 'monthly', 'yearly'];

export default function ManualCosts({ range }) {
  const ops = useOps();
  const [editing, setEditing] = React.useState(null); // item or {} for new
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;

  const items = ops.manualCosts;
  const occ = materializeManualCosts(items, range);

  // Materialized totals per month (INR via the month's first-day effective rate).
  const monthTotals = new Map();
  const unconvertible = new Set();
  for (const o of occ) {
    const day = `${o.monthKey}-01`;
    const inr = convertToINR(o.amount, o.currency, day, ops.fx);
    if (inr === null) { unconvertible.add(o.currency); continue; }
    monthTotals.set(o.monthKey, (monthTotals.get(o.monthKey) || 0) + inr);
  }
  const months = [...monthTotals.entries()].sort();
  const grandINR = months.reduce((s, [, v]) => s + v, 0);

  const del = async (item) => {
    try {
      await deleteManualCost(item.id);
      toast.success(`Deleted "${item.label}".`);
      setConfirmDelete(null);
      ops.reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.message || e}`);
    }
  };

  const csv = () => downloadCsv(
    `ops-manual-costs-${todayIso()}.csv`,
    [
      { key: 'label', label: 'Label' }, { key: 'category', label: 'Category' },
      { key: 'amount', label: 'Amount' }, { key: 'currency', label: 'Currency' },
      { key: 'cadence', label: 'Cadence' }, { key: 'startDate', label: 'Start' },
      { key: 'endDate', label: 'End' }, { key: 'notes', label: 'Notes' },
    ],
    items,
  );
  const txt = () => downloadTxtReport(`ops-manual-costs-${todayIso()}.txt`, {
    title: 'Healther Ops — Manual costs',
    meta: [
      `Range: ${range.from} → ${range.to}`,
      `Materialized total (INR): ${fmtINR(grandINR)}`,
    ],
    sections: [{
      heading: 'Materialized per month',
      columns: [{ key: 'month', label: 'Month' }, { key: 'inr', label: 'INR' }],
      rows: months.map(([m, v]) => ({ month: fmtMonthLabel(m), inr: Math.round(v) })),
    }, {
      heading: 'Items',
      columns: [
        { key: 'label', label: 'Label' }, { key: 'category', label: 'Category' },
        { key: 'amount', label: 'Amount' }, { key: 'cadence', label: 'Cadence' },
      ],
      rows: items.map((i) => ({ label: i.label, category: i.category, amount: `${i.amount} ${i.currency}`, cadence: i.cadence })),
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="Manual costs" subtitle="Store fees, infra baselines and subscriptions — materialized monthly">
        <Button size="sm" onClick={() => setEditing({})}><Plus className="size-3.5" /> Add cost</Button>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="Materialized in range" value={fmtINR(grandINR)}
          sub={`${months.length} month${months.length === 1 ? '' : 's'} in view`} />
        <KpiCard label="Active items" value={items.length}
          sub={`${items.filter((i) => i.cadence !== 'one-time').length} recurring`} />
        <KpiCard label="Latest month" value={months.length ? fmtINR(months[months.length - 1][1]) : '—'}
          sub={months.length ? fmtMonthLabel(months[months.length - 1][0]) : ''} />
      </div>

      {unconvertible.size > 0 && (
        <div className="mb-4 rounded-lg border border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-3.5 py-2 text-[12px] font-medium text-[hsl(var(--ai-foreground))]">
          No FX rate for {[...unconvertible].join(', ')} — those items are excluded from the INR totals above.
        </div>
      )}

      {items.length === 0 ? (
        <EmptyMeter title="No manual costs yet" sub="Add store fees, subscriptions and infra baselines so the Overview reflects true total spend." />
      ) : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          <Card className="gap-0 overflow-hidden py-0 xl:col-span-2">
            <CardHeader className="border-b py-4">
              <CardTitle className="text-[13.5px]">Items</CardTitle>
              <CardDescription className="text-[11.5px]">One row per cost; recurring items expand into the monthly table</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Label</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Cadence</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="pr-4 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="pl-4">
                          <div className="font-medium text-foreground">{i.label}</div>
                          {i.notes && <div className="text-[11px] text-muted-foreground">{i.notes}</div>}
                        </TableCell>
                        <TableCell><Badge variant="secondary" className="text-[10.5px]">{i.category}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-foreground">{fmtMoney(i.amount, i.currency)}</TableCell>
                        <TableCell className="text-muted-foreground">{i.cadence}</TableCell>
                        <TableCell className="tabular-nums text-[11.5px] text-muted-foreground">
                          {i.startDate}{i.endDate ? ` → ${i.endDate}` : i.cadence === 'one-time' ? '' : ' → ongoing'}
                        </TableCell>
                        <TableCell className="pr-4 text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="icon-xs" title="Edit" onClick={() => setEditing(i)}><Pencil className="size-3" /></Button>
                            <Button variant="ghost" size="icon-xs" title="Delete" onClick={() => setConfirmDelete(i)}><Trash2 className="size-3 text-destructive" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="gap-0 overflow-hidden py-0">
            <CardHeader className="border-b py-4">
              <CardTitle className="text-[13.5px]">Materialized monthly total</CardTitle>
              <CardDescription className="text-[11.5px]">Recurring costs expanded per month (INR)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {months.length === 0 ? (
                <div className="px-4 py-6 text-[12.5px] text-muted-foreground">No months in range.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Month</TableHead>
                      <TableHead className="pr-4 text-right">Total (INR)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {months.map(([m, v]) => (
                      <TableRow key={m}>
                        <TableCell className="pl-4 font-medium text-foreground">{fmtMonthLabel(m)}</TableCell>
                        <TableCell className="pr-4 text-right tabular-nums">{fmtINR(v)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {editing !== null && (
        <EditCostDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); ops.reload(); }}
        />
      )}

      {confirmDelete && (
        <Dialog open onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete "{confirmDelete.label}"?</DialogTitle>
              <DialogDescription>This removes the manual cost item; past months stop including it.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => del(confirmDelete)}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditCostDialog({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = React.useState({
    label: item.label || '', category: item.category || 'subscription',
    amount: item.amount != null ? String(item.amount) : '', currency: item.currency || 'INR',
    cadence: item.cadence || 'monthly', startDate: item.startDate || todayIso(),
    endDate: item.endDate || '', notes: item.notes || '',
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error('Give the cost a label.'); return; }
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Enter a valid amount.'); return; }
    if (!form.startDate) { toast.error('Pick a start date.'); return; }
    setBusy(true);
    try {
      await saveManualCost({
        id: item.id, label: form.label.trim(), category: form.category, amount: amt,
        currency: form.currency, cadence: form.cadence, startDate: form.startDate,
        endDate: form.endDate || null, notes: form.notes,
      });
      toast.success(isNew ? `Added "${form.label}".` : `Updated "${form.label}".`);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err?.message || err}`);
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add manual cost' : 'Edit manual cost'}</DialogTitle>
          <DialogDescription>Recurring items materialize per month at read time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mc-label">Label</Label>
            <Input id="mc-label" value={form.label} onChange={(e) => set('label')(e.target.value)} autoFocus placeholder="Apple Developer Program" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={set('category')}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Cadence</Label>
              <Select value={form.cadence} onValueChange={set('cadence')}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{CADENCES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mc-amt">Amount</Label>
              <Input id="mc-amt" type="number" step="any" min="0" value={form.amount}
                onChange={(e) => set('amount')(e.target.value)} placeholder="99" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={set('currency')}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{['INR', 'USD', 'EUR'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mc-start">Start date</Label>
              <Input id="mc-start" type="date" value={form.startDate} onChange={(e) => set('startDate')(e.target.value)} className="tabular-nums" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mc-end">End date (optional)</Label>
              <Input id="mc-end" type="date" value={form.endDate} onChange={(e) => set('endDate')(e.target.value)} className="tabular-nums" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mc-notes">Notes</Label>
            <Input id="mc-notes" value={form.notes} onChange={(e) => set('notes')(e.target.value)} placeholder="Optional context" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : isNew ? 'Add cost' : 'Save changes'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
